import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Upload,
  Sparkles,
  AlertCircle,
} from "lucide-react";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";
import { cn } from "../../lib/utils";

type UploadStage = "idle" | "uploading" | "processing" | "error";

export interface MagicUploadRow {
  UOM?: string;
  cgst?: number | null;
  description?: string;
  hsnCode?: string;
  igst?: number | null;
  itemName?: string;
  quantity?: number;
  sgst?: number | null;
  utgst?: number | null;
  unitPrice?: number;
}

interface MagicUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExtractedData?: (rows: MagicUploadRow[]) => void;
}

const PROCESSING_MESSAGES = [
  "Uploading your document...",
  "Reading document contents...",
  "Extracting text from pages...",
  "Analyzing document structure...",
  "Almost done, finalizing results...",
];

function MagicDocumentLoader() {
  return (
    <div className="relative mx-auto h-32 w-32">
      <div
        className="absolute inset-0 animate-spin rounded-full border-2 border-dashed border-[hsl(var(--brand)/0.25)]"
        style={{ animationDuration: "10s" }}
      />
      <div
        className="absolute inset-1 animate-spin rounded-full border border-[hsl(var(--brand-2)/0.2)]"
        style={{ animationDuration: "6s", animationDirection: "reverse" }}
      />

      <div className="absolute inset-3 animate-pulse rounded-full bg-[hsl(var(--brand)/0.08)]" />

      <div
        className="absolute inset-0 animate-spin"
        style={{ animationDuration: "5s" }}
      >
        <div className="absolute -top-0.5 left-1/2 -translate-x-1/2">
          <div
            className="animate-spin"
            style={{ animationDuration: "5s", animationDirection: "reverse" }}
          >
            <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--brand))]" />
          </div>
        </div>
      </div>
      <div
        className="absolute inset-0 animate-spin"
        style={{ animationDuration: "5s", animationDelay: "-2.5s" }}
      >
        <div className="absolute bottom-1 right-3">
          <div
            className="animate-spin"
            style={{
              animationDuration: "5s",
              animationDirection: "reverse",
              animationDelay: "-2.5s",
            }}
          >
            <Sparkles className="h-3 w-3 text-[hsl(var(--brand-2))]" />
          </div>
        </div>
      </div>

      <div className="absolute inset-5 flex items-center justify-center">
        <div className="relative h-[4.5rem] w-[3.25rem] overflow-hidden rounded-md border border-[hsl(var(--brand)/0.3)] bg-background shadow-[0_4px_20px_hsl(var(--brand)/0.15)]">
          <div className="space-y-1.5 p-2.5 pt-3">
            <div
              className="h-1 w-full rounded-full bg-[hsl(var(--brand-subtle))] animate-pulse"
              style={{ animationDelay: "0ms" }}
            />
            <div
              className="h-1 w-[85%] rounded-full bg-[hsl(var(--brand-subtle))] animate-pulse"
              style={{ animationDelay: "150ms" }}
            />
            <div
              className="h-1 w-full rounded-full bg-[hsl(var(--brand-subtle))] animate-pulse"
              style={{ animationDelay: "300ms" }}
            />
            <div
              className="h-1 w-[60%] rounded-full bg-[hsl(var(--brand-subtle))] animate-pulse"
              style={{ animationDelay: "450ms" }}
            />
            <div
              className="h-1 w-[75%] rounded-full bg-[hsl(var(--brand-subtle))] animate-pulse"
              style={{ animationDelay: "600ms" }}
            />
          </div>

          <div className="pointer-events-none absolute inset-x-0 top-0 h-10 animate-magic-scan bg-gradient-to-b from-[hsl(var(--brand)/0.05)] via-[hsl(var(--brand)/0.35)] to-transparent">
            <div className="absolute inset-x-0 top-0 h-px bg-[hsl(var(--brand))] shadow-[0_0_8px_hsl(var(--brand))]" />
          </div>
        </div>
      </div>
    </div>
  );
}

function MagicProgressLoader({
  progress,
  fileName,
  activeStep,
  stepCount,
}: {
  progress: number;
  fileName: string;
  activeStep: number;
  stepCount: number;
}) {
  const value = Math.min(100, Math.max(0, progress));

  return (
    <div className="space-y-4">
      <div className="relative px-1 pt-2 pb-1">
        <div
          className="pointer-events-none absolute -inset-x-1 top-1/2 h-8 -translate-y-1/2 rounded-full bg-[hsl(var(--brand)/0.12)] blur-md transition-all duration-700"
          style={{ width: `${Math.max(value, 10)}%` }}
        />

        <div className="relative h-2.5 overflow-hidden rounded-full border border-[hsl(var(--brand)/0.15)] bg-[hsl(var(--brand-subtle))]">
          <div
            className="relative h-full rounded-full bg-gradient-to-r from-[hsl(var(--brand))] via-[hsl(var(--brand-glow))] to-[hsl(var(--brand-2))] transition-[width] duration-700 ease-out"
            style={{ width: `${value}%` }}
          >
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute inset-y-0 w-1/2 -skew-x-12 animate-pulse bg-gradient-to-r from-transparent via-white/35 to-transparent" />
            </div>
          </div>
        </div>

        <div
          className="pointer-events-none absolute top-0 -translate-x-1/2 transition-[left] duration-700 ease-out"
          style={{ left: `${value}%` }}
        >
          <div className="relative flex h-6 w-6 items-center justify-center">
            <span className="absolute inset-0 animate-ping rounded-full bg-[hsl(var(--brand)/0.35)]" />
            <span className="absolute inset-1 rounded-full bg-gradient-to-br from-[hsl(var(--brand))] to-[hsl(var(--brand-2))] shadow-[0_0_10px_hsl(var(--brand)/0.6)]" />
            <Sparkles className="relative h-3 w-3 text-white" />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center">
        {Array.from({ length: stepCount }).map((_, index) => {
          const isActive = index === activeStep;
          const isComplete = index < activeStep;

          return (
            <div key={index} className="flex items-center">
              <div
                className={cn(
                  "rounded-full transition-all duration-500",
                  isActive &&
                    "h-2.5 w-2.5 bg-[hsl(var(--brand))] shadow-[0_0_8px_hsl(var(--brand)/0.55)]",
                  isComplete && "h-2 w-2 bg-[hsl(var(--brand)/0.5)]",
                  !isActive &&
                    !isComplete &&
                    "h-1.5 w-1.5 bg-[hsl(var(--brand-subtle))] ring-1 ring-[hsl(var(--brand)/0.2)]"
                )}
              />
              {index < stepCount - 1 && (
                <div
                  className={cn(
                    "mx-1.5 h-px w-5 transition-colors duration-500",
                    index < activeStep
                      ? "bg-[hsl(var(--brand)/0.4)]"
                      : "bg-[hsl(var(--brand-subtle))]"
                  )}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-3 text-xs">
        <span className="max-w-[68%] truncate text-muted-foreground">
          {fileName}
        </span>
        <span className="shrink-0 rounded-full bg-[hsl(var(--brand-subtle))] px-2 py-0.5 font-semibold tabular-nums text-[hsl(var(--brand))]">
          {Math.round(value)}%
        </span>
      </div>
    </div>
  );
}

export function MagicUploadDialog({
  open,
  onOpenChange,
  onExtractedData,
}: MagicUploadDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [stage, setStage] = useState<UploadStage>("idle");
  const [progress, setProgress] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const messageIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  const clearTimers = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    if (messageIntervalRef.current) {
      clearInterval(messageIntervalRef.current);
      messageIntervalRef.current = null;
    }
  };

  const resetState = () => {
    clearTimers();
    setFile(null);
    setStage("idle");
    setProgress(0);
    setMessageIndex(0);
    setErrorMessage("");
  };

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen && (stage === "uploading" || stage === "processing")) {
      return;
    }
    onOpenChange(nextOpen);
    if (!nextOpen) {
      resetState();
    }
  };

  const startProcessingAnimation = () => {
    setStage("processing");
    setProgress(25);

    progressIntervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 92) return prev;
        return prev + Math.random() * 2;
      });
    }, 1200);

    messageIntervalRef.current = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % PROCESSING_MESSAGES.length);
    }, 4000);
  };

  useEffect(() => {
    return () => clearTimers();
  }, []);

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const uploadedFile = event.target.files?.[0];
    if (!uploadedFile) return;

    const isAllowedFile =
      uploadedFile.type === "application/pdf" ||
      uploadedFile.type.startsWith("image/");

    if (!isAllowedFile) {
      toast.error("Please upload a PDF or image file");
      event.target.value = "";
      return;
    }

    setFile(uploadedFile);
    setStage("uploading");
    setProgress(8);
    setErrorMessage("");

    try {
      const formData = new FormData();
      formData.append("file", uploadedFile);

      startProcessingAnimation();

      const response: any = await apiService.post("/pdf-to-text", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 180000,
      });

      clearTimers();

      const rows: MagicUploadRow[] = response?.data?.rows ?? [];

      if (!rows.length) {
        throw new Error("No asset rows found in the uploaded document");
      }

      setProgress(100);
      onExtractedData?.(rows);
      onOpenChange(false);
      resetState();
    } catch (error) {
      clearTimers();
      console.error("Magic upload failed:", error);
      setStage("error");
      setProgress(0);
      setErrorMessage(
        (error as { message?: string })?.message ||
          "Failed to process the uploaded document. Please try again."
      );
      toast.error("Failed to process document");
    } finally {
      event.target.value = "";
    }
  };

  const isLoading = stage === "uploading" || stage === "processing";
  const canClose = stage === "idle" || stage === "error";
  const statusMessage =
    stage === "uploading"
      ? "Preparing your document for upload..."
      : PROCESSING_MESSAGES[messageIndex];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className={cn("max-w-lg", isLoading && "[&>button]:hidden")}
        onInteractOutside={(e) => {
          if (!canClose) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (!canClose) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="rounded-lg bg-gradient-to-r from-[hsl(var(--brand))] to-[hsl(var(--brand-2))] p-1.5">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            Magic Upload
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {stage === "idle" && (
            <Label htmlFor="magic-upload-input" className="block cursor-pointer">
              <div className="rounded-lg border-2 border-dashed border-[hsl(var(--brand)/0.25)] p-8 text-center transition-colors hover:border-[hsl(var(--brand)/0.5)] hover:bg-[hsl(var(--brand-subtle))]/40">
                <div className="flex flex-col items-center gap-3">
                  <div className="rounded-full bg-gradient-to-r from-[hsl(var(--brand))] to-[hsl(var(--brand-2))] p-3">
                    <Upload className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="font-medium">Upload PDF or Image</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Drag & drop or click to browse
                    </p>
                  </div>
                  <Button type="button" variant="outline" size="sm" asChild>
                    <span>Choose File</span>
                  </Button>
                </div>
              </div>
              <Input
                id="magic-upload-input"
                type="file"
                accept=".pdf,image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
            </Label>
          )}

          {isLoading && file && (
            <div className="space-y-6 py-4">
              <MagicDocumentLoader />

              <div className="space-y-2 text-center">
                <h3 className="font-semibold">Processing Document</h3>
                <p
                  key={messageIndex}
                  className="animate-in fade-in slide-in-from-bottom-1 text-sm text-muted-foreground duration-500"
                >
                  {statusMessage}
                </p>
                <p className="text-xs text-muted-foreground">
                  This may take 30 seconds or more. Please wait...
                </p>
              </div>

              <MagicProgressLoader
                progress={progress}
                fileName={file.name}
                activeStep={messageIndex}
                stepCount={PROCESSING_MESSAGES.length}
              />
            </div>
          )}

          {stage === "error" && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <span className="font-medium">Processing failed</span>
              </div>
              <p className="text-sm text-muted-foreground">{errorMessage}</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => handleClose(false)}>
                  Close
                </Button>
                <Button onClick={resetState}>Try Again</Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
